import { ref, onUnmounted } from 'vue'

// --- Geteilter Zustand (Singleton-Pattern) ---
// Diese Variablen existieren nur einmal in der gesamten Anwendung, um die Performance zu optimieren.
const now = ref(Date.now()) // Der aktuelle Zeitstempel, reaktiv.
let intervalId = null // Die ID des globalen Timers.
let activeInstances = 0 // Zählt, wie viele Komponenten den Timer gerade aktiv nutzen.

/**
 * Aktualisiert den globalen Zeitstempel.
 */
const updateNow = () => {
  now.value = Date.now()
}

/**
 * Startet den globalen Timer, wenn er nicht bereits läuft.
 */
const startGlobalTimer = () => {
  if (intervalId) return
  updateNow()
  intervalId = setInterval(updateNow, 1000) // Aktualisiert jede Sekunde.
}

/**
 * Stoppt den globalen Timer, wenn er nicht mehr benötigt wird.
 */
const stopGlobalTimer = () => {
  if (!intervalId) return
  clearInterval(intervalId)
  intervalId = null
}
// --- Ende Geteilter Zustand ---

/**
 * Ein Vue Composable, das einen reaktiven Countdown bereitstellt.
 * Es nutzt einen einzigen, globalen Timer, um zu vermeiden, dass jede Komponente
 * ihren eigenen Timer startet, was die Performance verbessert.
 */
export function useBetCountdown() {
  // --- Instanz-spezifische Logik ---
  // Wenn die erste Komponente diesen Composable nutzt, starte den globalen Timer.
  if (activeInstances === 0) {
    startGlobalTimer()
  }
  activeInstances++

  // Wenn die Komponente zerstört wird (unmounted), reduziere den Zähler.
  // Wenn es die letzte aktive Komponente war, wird der globale Timer gestoppt, um Ressourcen zu sparen.
  onUnmounted(() => {
    activeInstances--
    if (activeInstances === 0) {
      stopGlobalTimer()
    }
  })
  // --- Ende Instanz-spezifische Logik ---

  /**
   * Berechnet die verbleibende Zeit in Millisekunden bis zu einem Endzeitpunkt.
   * @param {number} endTime - Der Endzeitstempel in Millisekunden.
   * @returns {number} Die verbleibenden Millisekunden.
   */
  const getRemainingMs = (endTime) => {
    if (!endTime) return 0
    // `now.value` ist der reaktive, globale Zeitstempel.
    return Math.max(0, new Date(endTime).getTime() - now.value)
  }

  /**
   * Formatiert die verbleibende Zeit in einen "TT:HH:MM:SS"-String.
   * @param {number} endTime - Der Endzeitstempel in Millisekunden.
   * @returns {string} Der formatierte Countdown-String.
   */
  const formatCountdown = (endTime) => {
    const totalSeconds = Math.floor(getRemainingMs(endTime) / 1000)
    const days = Math.floor(totalSeconds / 86400)
    const hours = Math.floor((totalSeconds % 86400) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    const pad = (num) => String(num).padStart(2, '0')
    return `${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
  }

  // Gibt die Helferfunktionen zurück, die von den Komponenten verwendet werden können.
  return { getRemainingMs, formatCountdown }
}